<script setup lang="ts">
import { AggregateBusinessFilterProperty, FilterType, isFetchRequiredFilterType } from '@/coreDomainsModule/entities/filter';
import { FilterInfoState } from './interface/uiState';
import { IPagingOptions } from '@/coreDomainsModule/paging';
import BaseSelectInput from './BaseSelectInput.vue';
import { ref, watch, computed, nextTick } from 'vue';
import RowCheckBox from '@/components/atom/RowCheckBox.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { i18n } from '@/i18n';
import { useSelectInputStore } from './useSelectInputStore';
import { storeToRefs } from 'pinia';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { lineClampSizes } from '@/coreDomainsModule/lineClamp';
import { SelectInputViewModel } from './selectInputViewModel';
import { FilterModalType } from '../filterModal/filterModal';

const vm = new SelectInputViewModel();
const t = i18n.t;

const props = withDefaults(
  defineProps<{
    index: number;
    selectedList: string[];
    placeholder: string;
    label: string;
    disabled?: boolean;
    filterType: FilterType;
    isOpenProp?: boolean;
    propAggregateBusinessFilterProperty?: AggregateBusinessFilterProperty;
    // 以下は使用元で選択肢を指定してfetch処理を括り出す場合に使用
    propOptions?: string[];
    propPagingState?: FilterInfoState;
    propPagingOptions?: IPagingOptions;
    propSelectedIndexes?: number[];
    propFilteredOptions?: string[];
    isError?: boolean;
    errorMessage?: string;
    useTeleport?: boolean;
    filterModalType?: FilterModalType;
    useSearchInput?: boolean;
    useReset?: boolean;
  }>(),
  {
    propAggregateBusinessFilterProperty: undefined,
    /**
     * propOption初期化 defaultでArray, Objectを指定する場合ファクトリー関数を用いる必要がある
     */
    propOptions: () => [] as string[],
    propPagingState: undefined,
    propPagingOptions: undefined,
    propSelectedIndexes: undefined,
    propFilteredOptions: undefined,
    errorMessage: '',
    filterModalType: 'deletion-business',
    useSearchInput: true,
    useReset: true,
  }
);

const store = useSelectInputStore(props.filterType, props.filterModalType)();
const { state: baseSelectState } = storeToRefs(store);

const emit = defineEmits<{
  (e: 'click:selectInput'): void;
  (e: 'update:modelValue', value: string[]): void;
  (e: 'update:valueWithIndexes', value: string[], indexes: number[]): void;
  (e: 'click:seeMore'): void;
  (e: 'close'): void;
  (e: 'search', keyword: string);
}>();

const selectedList = ref<string[]>([...props.selectedList]);
const selectedIndexes = ref<number[]>(props.propSelectedIndexes ?? []);
const selectedRepresent = ref<string>(props.selectedList[0] ?? '');

const hasSelectedItems = computed(() => selectedList.value.length > 0);

watch(
  () => props.selectedList,
  () => {
    selectedRepresent.value = props.selectedList[0];
    selectedList.value = props.selectedList;
  }
);

watch(
  () => props.propSelectedIndexes,
  () => {
    selectedIndexes.value = props.propSelectedIndexes ?? [];
  }
);

/**
 * 他のポップアップ要素の位置を再計算する
 */
const recalculatePopupPositions = () => {
  // VueDatePickerなどの固定位置要素を再配置
  nextTick(() => {
    // リサイズイベントを発火してポップアップ位置を再計算
    window.dispatchEvent(new Event('resize'));

    // VueDatePickerが存在する場合は直接再配置
    const datePickerMenus = document.querySelectorAll('.dp__menu');
    datePickerMenus.forEach((menu) => {
      const menuElement = menu as HTMLElement;
      if (menuElement.style.display !== 'none') {
        // メニューが表示されている場合、位置を再計算
        const trigger = document.querySelector('.dp__input_wrap');
        if (trigger) {
          const rect = trigger.getBoundingClientRect();
          menuElement.style.left = `${rect.left}px`;
          menuElement.style.top = `${rect.top - menuElement.offsetHeight - 8}px`;
        }
      }
    });
  });
};

/**
 * 選択されたオプションのリストを親コンポーネントに通知します。
 * @param option - 選択されたオプション
 * @param index - 選択されたオプションのインデックス
 */
const selectOption = (option: string, index?: number) => {
  const optionIndex = selectedList.value.indexOf(option);
  if (optionIndex > -1) {
    selectedList.value.splice(optionIndex, 1);
  } else {
    selectedList.value.push(option);
  }
  // インデックスが指定されている場合は、選択されたオプションのインデックスを保持する
  if (index !== undefined) {
    let overallIndex = index;
    if (props.propFilteredOptions) {
      // フィルタしている場合、そのままだとフィルタ後のものに対してのindexになるので変更する
      overallIndex = props.propOptions.indexOf(option);
    }
    const indexIndex = selectedIndexes.value.indexOf(overallIndex);
    if (indexIndex > -1) {
      selectedIndexes.value.splice(indexIndex, 1);
    } else {
      selectedIndexes.value.push(overallIndex);
    }
  }
  selectedRepresent.value = selectedList.value[0] ?? '';
  emit('update:modelValue', selectedList.value);
  if (index !== undefined) emit('update:valueWithIndexes', selectedList.value, selectedIndexes.value);

  // 選択変更後にポップアップ位置を再計算
  recalculatePopupPositions();
};

/**
 * すべてのオプションを選択状態にします。
 */
const selectAllOptions = () => {
  let allOptions: string[] = [];
  if (props.propFilteredOptions ? props.propFilteredOptions.length > 0 : props.propOptions.length > 0) {
    allOptions = [...(props.propFilteredOptions ?? props.propOptions)];
  } else if (isFetchRequiredFilterType(props.filterType) && baseSelectState.value.pagingState.type === 'success') {
    allOptions = [...baseSelectState.value.pagingState.data];
  } else if (!isFetchRequiredFilterType(props.filterType) && baseSelectState.value.options.length > 0) {
    allOptions = [...baseSelectState.value.options];
  }

  if (allOptions.length > 0) {
    selectedList.value = [...allOptions];
    // 全オプションに対応するインデックスを生成 (0 から始まる連番)
    selectedIndexes.value = allOptions.map((_, i) => i);
    selectedRepresent.value = selectedList.value[0] ?? '';

    emit('update:modelValue', selectedList.value);
    emit('update:valueWithIndexes', selectedList.value, selectedIndexes.value);

    // 全選択後にポップアップ位置を再計算
    recalculatePopupPositions();
  }
};

/**
 * 選択状態に応じて、選択を解除するか、すべて選択します。
 */
const handleReset = () => {
  if (hasSelectedItems.value) {
    resetSelectedOption();
  } else {
    selectAllOptions();
  }
};

/**
 * 選択されたオプションのリストをリセットします。
 */
const resetSelectedOption = () => {
  selectedList.value = [];
  selectedIndexes.value = [];
  selectedRepresent.value = '';

  emit('update:modelValue', []);
  emit('update:valueWithIndexes', [], []);

  // リセット後にポップアップ位置を再計算
  recalculatePopupPositions();
};

/**
 * 選択されたオプションを削除します。
 * @param option - 削除するオプション
 * @param index - 削除するオプションのインデックス
 */
const removeSelectedOption = (option: string, index?: number) => {
  selectOption(option, index);
  emit('close');
};

const confirmButtonBottom = ref<number>(0);

/**
 * emitから確定ボタンのスタイルを決定する関数
 * @param bottom - 下要素(SelectedList)の高さ
 */
const setConfirmButtonStyle = (bottom?: number) => {
  confirmButtonBottom.value = bottom ?? 0;
};
</script>
<template>
  <BaseSelectInput
    :index="index"
    :selected-represent="selectedRepresent"
    :placeholder="placeholder"
    :label="label"
    :disabled="disabled"
    :filter-type="filterType"
    :is-open-prop="isOpenProp"
    :is-continue-opening="true"
    :prop-aggregate-business-filter-property="propAggregateBusinessFilterProperty"
    :prop-options="propFilteredOptions ?? propOptions"
    :prop-original-options="propOptions"
    :prop-paging-state="propPagingState"
    :prop-paging-options="propPagingOptions"
    :is-error="isError"
    :error-message="errorMessage"
    :use-teleport="useTeleport"
    :filter-modal-type="filterModalType"
    :reset-button-text="hasSelectedItems ? t('選択を外す') : t('全選択する')"
    :use-search-input="useSearchInput"
    :use-reset="useReset"
    @click:select-input="() => $emit('click:selectInput')"
    @reset="handleReset"
    @update="(e, index) => selectOption(e, index)"
    @click:see-more="() => $emit('click:seeMore')"
    @close="() => $emit('close')"
    @selected-list-resized="
      (bottom) => {
        setConfirmButtonStyle(bottom);
      }
    "
    @search="(keyword) => $emit('search', keyword)"
  >
    <template #selectedListTemplate="selectedListProp">
      <div v-if="selectedList.length > 0" class="select-box__list--wrapper" :style="selectedListProp.scroll ? { overflow: 'scroll' } : {}">
        <template v-for="selected in selectedList" :key="selected.key">
          <div class="select-box__list--item">
            <div class="select-box__list--item--text" :style="{ WebkitLineClamp: lineClampSizes['XS'] }">
              {{ vm.displayValue(filterType, selected) }}
            </div>
            <div v-if="selectedListProp.isOpen" class="select-box__list--item--close" @click="selectOption(selected)">
              <SvgLoader name="close" />
            </div>
            <div v-else class="select-box__list--item--close" @click="removeSelectedOption(selected)">
              <SvgLoader name="close" />
            </div>
          </div>
        </template>
      </div>
    </template>
    <template #selectBoxOptionCheckboxTemplate="selectBoxOptionInnerProp">
      <RowCheckBox :is-checked="selectedList.includes(selectBoxOptionInnerProp.option)" />
    </template>
    <template #confirmButtonTemplate="confirmButtonProp">
      <div class="select-box__confirm--wrapper" :style="{ bottom: `${confirmButtonBottom}px` }">
        <TextButton :button-style="'btn-primary'" size="btn-sm" :on-click="confirmButtonProp.click" class="select-box__confirm--button">
          {{ t('確定') }}
        </TextButton>
      </div>
    </template>
  </BaseSelectInput>
</template>
<style scoped lang="scss">
.select-box {
  &__list {
    &--wrapper {
      max-height: 132px;
      overflow-y: auto;
      width: 100%;
      padding: 8px 4px 0 4px;

      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      background-color: $surfaceDefault;
    }

    &--item {
      width: fit-content;
      min-height: 24px;
      height: fit-content;
      padding: 4px;

      border: 1px solid $borderSecondary;
      border-radius: 4px;

      display: grid;
      grid-template-columns: auto 16px;
      justify-content: space-between;
      align-items: center;
      gap: 4px;
      background-color: white;

      color: $textSecondary;
      font-size: 12px;
      font-weight: 700;
      line-height: 12px;

      &--text {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      &--close {
        cursor: pointer;
      }
    }
  }
  &__confirm {
    &--wrapper {
      display: flex;
      justify-content: end;

      padding: 0 8px 8px 8px;

      position: sticky;
      background: white;
    }
    &--button {
      font-size: 12px;
      height: 20px;
      padding-top: 4px;
      padding-bottom: 4px;
    }
  }
}
</style>
